import React from 'react';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { eventColors } from '../Event/eventTheme';
import { deltaColor, formatScore } from './duelsTheme';
import { duelTextStyles } from './DuelsUi';

const DeltaArrow = ({ up, size, color }: { up: boolean; size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 12 12'>
		<Path
			d={up ? 'M6 10.5V1.5M6 1.5L2 5.5M6 1.5L10 5.5' : 'M6 1.5V10.5M6 10.5L2 6.5M6 10.5L10 6.5'}
			stroke={color}
			strokeWidth={1.8}
			strokeLinecap='round'
			strokeLinejoin='round'
			fill='none'
		/>
	</Svg>
);

type StatTileProps = {
	icon: ImageSourcePropType;
	label: string;
	width: number;
	scale: number;
	value?: number | null;
	delta?: number | null;
	nowText?: string | null;
};

export const DuelStatTile = ({
	icon,
	label,
	width,
	scale,
	value,
	delta,
	nowText,
}: StatTileProps) => {
	const showsDelta = delta !== undefined;
	return (
		<View
			style={[
				styles.tile,
				{ width, columnGap: 8 * scale, padding: 8 * scale, borderRadius: 8 * scale },
			]}
		>
			<Image source={icon} style={{ width: 28 * scale, height: 28 * scale }} resizeMode='contain' />
			<View style={styles.flex}>
				<Text numberOfLines={1} style={[duelTextStyles.body, { fontSize: 11 * scale }]}>
					{label}
				</Text>
				{showsDelta ? (
					delta === null ? (
						<Text style={[duelTextStyles.muted, { fontSize: 15 * scale }]}>—</Text>
					) : (
						<View style={[styles.row, { columnGap: 2 * scale }]}>
							{delta !== 0 ? (
								<DeltaArrow up={delta > 0} size={12 * scale} color={deltaColor(delta)} />
							) : null}
							<Text
								style={[duelTextStyles.title, { fontSize: 15 * scale, color: deltaColor(delta) }]}
							>
								{formatScore(Math.abs(delta))}
							</Text>
						</View>
					)
				) : (
					<Text style={[duelTextStyles.title, { fontSize: 15 * scale }]}>{formatScore(value)}</Text>
				)}
				{nowText ? (
					<Text numberOfLines={1} style={[duelTextStyles.muted, { fontSize: 10 * scale }]}>
						{nowText}
					</Text>
				) : null}
			</View>
		</View>
	);
};

type BannerProps = {
	source: ImageSourcePropType;
	aspect: number;
	width: number;
	scale: number;
	textWidth: number;
	children: React.ReactNode;
};

export const DuelBanner = ({ source, aspect, width, scale, textWidth, children }: BannerProps) => (
	<View
		style={[
			styles.banner,
			{
				width,
				height: width / aspect,
				borderRadius: 12 * scale,
				borderWidth: Math.max(1, 1 * scale),
			},
		]}
	>
		<Image source={source} style={styles.bannerImage} resizeMode='cover' />
		<View style={[styles.bannerText, { width: textWidth, paddingRight: 12 * scale }]}>
			{children}
		</View>
	</View>
);

const styles = StyleSheet.create({
	tile: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: 'rgba(0,0,0,0.28)',
		borderWidth: 1,
		borderColor: 'rgba(224,135,18,0.45)',
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	banner: {
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'flex-end',
		borderColor: eventColors.countdownBorder,
	},
	bannerImage: {
		...StyleSheet.absoluteFillObject,
		width: undefined,
		height: undefined,
	},
	bannerText: {
		alignItems: 'center',
	},
});
